import { useEffect, useState } from "react";
import TablaClientesVista from "../vista/tablaClientesVista";
import type { ICliente } from "../../../modelos/Clientes/entidades/ICliente";
import { getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { columnasTablaCliente } from "../vista/constantesTablaClientes";
import FormularioClienteVista from "../vista/formularioClienteVista";
import { estadoInicialCliente, estadoInicialFiltroCliente, type IClientesCVProps, type ITipoIdentificacion } from "../interfaces";
import React from "react";
import { ClienteModelo } from "../../../modelos/Clientes";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { Box, Tabs } from "@mantine/core";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import type { IContactoCliente } from "../../../modelos/Clientes/entidades/IContactoCliente";
import { useProyecto } from "../../proyectos/contextoProyecto";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import type { IClienteHistorial } from "../../../modelos/ClientesContratos/entidades/IClienteHistorial";
import type { IFiltroCliente } from "../../../modelos/Clientes/entidades/IFiltroCliente";

const ClientesCV: React.FunctionComponent<IClientesCVProps> = ({ }) => {
  const TAMANIO_PAGINA = 8;
  const modeloCliente = new ClienteModelo();
  const modeloProyecto = new ProyectoModelo();

  const { mostrarNotificacion } = useNotificaciones();
  const { proyecto, setProyecto } = useProyecto();
  const { manejarCargando } = useCargando();

  const [listaClientes, setListaClientes] = useState<IClienteHistorial[]>([]);
  const [indicePagina, setIndicePagina] = useState(0);
  const [totalClientes, setTotalClientes] = useState(0);
  const [habilitarFiltros, setHabilitarFiltros] = useState(false);
  const [filtro, setFiltro] = useState<IFiltroCliente>(estadoInicialFiltroCliente);

  const [cliente, setCliente] = useState<ICliente>(estadoInicialCliente);
  const [clienteConsultadoIdentificacion, setClienteConsultadoIdentificacion] = useState(false);
  const [tiposIdentificacion, setTiposIdentificacion] = useState<ITipoIdentificacion[]>([]);
  const [enRenovacionCliente, setEnRenovacionCliente] = useState(false);
  const [errores, setErrores] = useState<Partial<Record<keyof ICliente, string | null>>>({});
  const [erroresContactos, setErroresContactos] = useState<
    Record<number, Partial<Record<keyof IContactoCliente, string | null>>>
  >({});

  useEffect(() => {
    consultarTiposIdentificacion();
  }, []);

  const consultarTiposIdentificacion = async () => {
    const tipos = [{
      IdTipoIdentificacion: 1,
      TipoIdentificacion: "Cédula de Ciudadanía",
      Activo: true
    }, {
      IdTipoIdentificacion: 2,
      TipoIdentificacion: "NIT",
      Activo: true
    },
    {
      IdTipoIdentificacion: 3,
      TipoIdentificacion: "Cédula Jurídica",
      Activo: true
    },
      {
      IdTipoIdentificacion: 4,
      TipoIdentificacion: "RUT",
      Activo: true
    }
    ]
    setTiposIdentificacion(tipos);
    return tipos;
  }

  const consultarClienteActivo = async (clienteId: number) => {
    try {
      manejarCargando(true);
      if (clienteId !== 0 && clienteId !== null && clienteId !== undefined) {
        const clienteActivo = await modeloCliente.obtenerClientePorId(clienteId);
        setCliente(clienteActivo.Data);
        setClienteConsultadoIdentificacion(true);
      }
    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar cliente',
        'No fue posible consultar la información del cliente. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  }

  const consultarClientePorIdentificacion = async (identificacion: string) => {
    try {
      manejarCargando(true);
      setClienteConsultadoIdentificacion(true);
      if (identificacion !== '') {
        const clienteActivo = await modeloCliente.obtenerListaClientesCompuesto(
          { Identificacion: identificacion },
          { Page: 1, Size: 1 });
        if (clienteActivo.Data.length > 0) {
          if (clienteActivo.Data[0].Nombre !== '') agregarError('Nombre', '')
          setCliente(clienteActivo.Data[0]);
        }
        else {
          mostrarNotificacion(
            EnumTipoNotificacion.Information,
            'El cliente no existe',
            'Crea un nuevo cliente o ingresa un número de identificacición de un cliente existente.',
            6000
          );
        }
      }
    }
    catch {
      setClienteConsultadoIdentificacion(false);
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar cliente por identificación',
        'No fue posible consultar el cliente por identificación. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  }

  const consultarClientes = async (page: number, size: number) => {
    try {
      manejarCargando(true);
      if (proyecto.Id) {
        const clientes = await modeloCliente.obtenerListaHistorialClientesCompuesto(
          proyecto.Id,
          { Page: page + 1, Size: size }
        );
        const tipos = await consultarTiposIdentificacion();
        setListaClientes(clientes.Data.map(cl => {
          return {
            ...cl,
            TipoIdentificacion: tipos.find(t => t.IdTipoIdentificacion === cl.TipoIdentificacionId)?.TipoIdentificacion || '',
          }
        }));
        setTotalClientes(clientes.Pagination.Total ?? clientes.Data.length);
      }
    }
    catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al consultar clientes',
        'No fue posible consultar los clientes. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  }

  const manejarCambioValorCliente = (campo: keyof ICliente, valor: ICliente[keyof ICliente]) => {
    if (campo === 'TipoIdentificacionId' && (valor === 1 || valor === 0)) {
      setCliente({
        ...cliente,
        RazonSocial: ''
      })
    }
    if (campo === 'Identificacion') {
      setCliente({
        ...cliente,
        Nombre: ''
      })
      setClienteConsultadoIdentificacion(false);
    }
    setCliente((prevCliente) => ({
      ...prevCliente,
      [campo]: valor
    }));
  }


  const agregarError = async (campo: keyof ICliente, error: string) => {
    setErrores((prevErrores) => ({
      ...prevErrores,
      [campo]: error
    }));

  }

  const agregarErrorContacto = (posicion: number, campo: keyof IContactoCliente, error: string) => {
    setErroresContactos(prev => ({
      ...prev,
      [posicion]: {
        ...prev[posicion],
        [campo]: error
      }
    }));
  };


  const validarCamposCliente = () => {
    let esValido = true;

    if (!cliente.Nombre.trim()) {
      agregarError("Nombre", "Este campo es requerido");
      esValido = false;
    }

    if (cliente.TipoIdentificacionId === 0) {
      agregarError("TipoIdentificacionId", "Selecciona un tipo de identificaciÃ³n");
      esValido = false;
    }

    if (!cliente.Identificacion.trim()) {
      agregarError("Identificacion", "Este campo es requerido");
      esValido = false;
    }

    if (cliente.RazonSocial === '' && cliente.TipoIdentificacionId === 2) {
      agregarError("RazonSocial", "Este campo es requerido");
      esValido = false;
    }

    cliente.ContactoClientes.forEach((contacto, index) => {
      if (!contacto.Nombre.trim() && index === 0) {
        agregarErrorContacto(index, "Nombre", "El nombre es requerido");
        esValido = false;
      } else {
        agregarErrorContacto(index, "Nombre", "");
      }
      if (!contacto.Email.trim() && index === 0) {
        agregarErrorContacto(index, "Email", "El correo es requerido");
        esValido = false;
      } else {
        agregarErrorContacto(index, "Email", "");
      }
      if (!contacto.Telefono.trim() && index === 0) {
        agregarErrorContacto(index, "Telefono", "El telÃ©fono es requerido");
        esValido = false;
      } else {
        agregarErrorContacto(index, "Telefono", "");
      }
    });

    if (!esValido) {
      mostrarNotificacion(
        EnumTipoNotificacion.Warning,
        "Campos requeridos (*)",
        "Por favor completa todos los campos antes de continuar.",
        3000
      );
    }

    return esValido;
  };


  const crearCliente = async () => {
    if (!validarCamposCliente()) return;
    manejarCargando(true);
    try {

      cliente.ContactoClientes = cliente.ContactoClientes.filter(
        cc => cc.Nombre.trim() !== '' || cc.Email.trim() !== '' || cc.Telefono.trim() !== ''
      );
      let clienteCreado;
      try {
        const clienteRespuesta = await modeloCliente.crearCliente(cliente);
        if (clienteRespuesta.NotificationType === EnumTipoNotificacion.Success && clienteRespuesta.Data) {
          clienteCreado = clienteRespuesta.Data;
          setCliente(clienteCreado);
        }
        if (clienteRespuesta.Messages.length > 0) {
          clienteRespuesta.Messages.forEach(mensaje =>
            mostrarNotificacion(
              clienteRespuesta.NotificationType,
              '',
              mensaje,
              4000
            )
          );
        }
        if (!clienteCreado) {
          throw new Error("No se pudo crear el cliente.");
        }
      } catch {
        mostrarNotificacion(
          EnumTipoNotificacion.Error,
          'Error al crear cliente',
          'No se pudo crear el cliente. Por favor verifica los datos e intenta nuevamente.',
          100000
        );
        return;
      }
      try {
        const respuestaEdicionProyecto = await modeloProyecto.editarProyecto({
          ...proyecto,
          // DepartamentoEstadoId: 1,
          // PaisId: 0,
          ClienteId: clienteCreado.Id,
          Cliente: {
            ...cliente,
            Id: clienteCreado.Id
          }
        });

        if (respuestaEdicionProyecto.NotificationType === EnumTipoNotificacion.Success && respuestaEdicionProyecto.Data && respuestaEdicionProyecto.Data.ClienteId) {
          setProyecto({
            ...proyecto,
            ClienteId: respuestaEdicionProyecto.Data.ClienteId
          })
          consultarClienteActivo(respuestaEdicionProyecto.Data.ClienteId);
        }
      } catch {
        mostrarNotificacion(
          EnumTipoNotificacion.Warning,
          'Cliente creado, pero no asociado',
          'El cliente fue creado exitosamente, pero no se pudo asociar al proyecto. Por favor consulta el cliente creado e intenta asociarlo nuevamente.',
          100000
        );
      }

    } catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'No se pudo completar la acción',
        'Ocurrió un problema al conectar con el servidor. Por favor intenta nuevamente en unos minutos.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  }

  const editarCliente = async () => {
    if (!validarCamposCliente()) return;
    manejarCargando(true);
    try {
      cliente.ContactoClientes = cliente.ContactoClientes.filter(
        cc => cc.Nombre.trim() !== '' || cc.Email.trim() !== '' || cc.Telefono.trim() !== ''
      );
      let clienteEditado;
      try {
        const clienteRespuesta = await modeloCliente.editarCliente({
          ...cliente,
          CreadoUtc: cliente.CreadoUtc
        });
        if (clienteRespuesta.NotificationType === EnumTipoNotificacion.Success && clienteRespuesta.Data) {
          clienteEditado = clienteRespuesta.Data;
          setCliente(clienteEditado);
        }
        if (clienteRespuesta.Messages.length > 0) {
          clienteRespuesta.Messages.forEach(mensaje =>
            mostrarNotificacion(
              clienteRespuesta.NotificationType,
              '',
              mensaje,
              4000
            )
          );
        }
        if (!clienteEditado) {
          throw new Error("No se pudo editar el cliente.");
        }
      } catch {
        mostrarNotificacion(
          EnumTipoNotificacion.Error,
          'No se pudo editar el cliente',
          'No fue posible actualizar la información del cliente. Por favor revisa los datos e intenta nuevamente.',
          100000
        );
        return;
      }

      if (proyecto.ClienteId !== clienteEditado.Id) {
        try {
          const respuestaEdicionProyecto = await modeloProyecto.editarProyecto({
            ...proyecto,
            ClienteId: clienteEditado.Id,
            Cliente: undefined
          });

          if (respuestaEdicionProyecto.NotificationType === EnumTipoNotificacion.Success && respuestaEdicionProyecto.Data && respuestaEdicionProyecto.Data.ClienteId) {
            setProyecto({
              ...proyecto,
              ClienteId: respuestaEdicionProyecto.Data.ClienteId
            })
            consultarClienteActivo(respuestaEdicionProyecto.Data.ClienteId);
          }
        } catch {
          mostrarNotificacion(
            EnumTipoNotificacion.Warning,
            'Cliente actualizado, pero no asociado',
            'La edición del cliente fue exitosa, pero no se pudo asociar al proyecto. Por favor consulta el cliente actualizado e intenta asociarlo nuevamente.',
            100000
          );
        }
      }

    } catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'No se pudo completar la acción',
        'Ocurrió un inconveniente al conectar con el servidor. Por favor intenta de nuevo en unos minutos.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  }

  const crearEditarCliente = () => {
    cliente.TipoIdentificacionNombre = "CC"
    if (cliente.Id === 0) {
      crearCliente();
    }
    else {
      editarCliente();
    }
  }

  const manejarHabilitarFiltros = (habilitar: boolean) => {
    setHabilitarFiltros(habilitar)
  }

  const manejarCambioFiltroCliente = (campo: keyof IFiltroCliente, valor: IFiltroCliente[keyof IFiltroCliente]) => {
    setFiltro((prevFiltro) => ({
      ...prevFiltro,
      [campo]: valor
    }));
  }

  const tabla = useReactTable({
    data: listaClientes,
    columns: [],
    manualPagination: true,
    pageCount: Math.ceil(totalClientes / TAMANIO_PAGINA),
    state: { pagination: { pageIndex: indicePagina, pageSize: TAMANIO_PAGINA } },
    onPaginationChange: updater => {
      if (typeof updater === "function") {
        const newState = updater({ pageIndex: indicePagina, pageSize: TAMANIO_PAGINA });
        setIndicePagina(newState.pageIndex);
        consultarClientes(newState.pageIndex, newState.pageSize);
      } else {
        setIndicePagina(updater.pageIndex ?? 0);
        consultarClientes(updater.pageIndex ?? 0, TAMANIO_PAGINA);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const columns = columnasTablaCliente({
    manejarEdicionCliente: (cliente) => setCliente(cliente),
    tabla,
  });

  tabla.setOptions((prev) => ({ ...prev, columns }));


  const renovarCliente = (estado: boolean) => {
    setEnRenovacionCliente(estado);
    if (estado) {
      setCliente(estadoInicialCliente);
      setErrores({});
      setClienteConsultadoIdentificacion(false);
    } else {
      if (proyecto.ClienteId)
        consultarClienteActivo(proyecto.ClienteId);
    }
  }

  const limpiarCampos = () => {
    setCliente(estadoInicialCliente);
    setErrores({});
    setEnRenovacionCliente(false);
    setClienteConsultadoIdentificacion(false);
  }


  return (
    <Box p='md'>
      <Tabs keepMounted={false} variant="outline" defaultValue="ClienteActivo">
        <Tabs.List >
          <Tabs.Tab value="ClienteActivo" >
            Cliente Activo
          </Tabs.Tab>
          <Tabs.Tab value="Historico" >
            Historico
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="ClienteActivo">
          <Box pt="md">
            <FormularioClienteVista
              agregarEditarCliente={crearEditarCliente}
              clienteSeleccionado={cliente}
              manejarCambioValorCliente={manejarCambioValorCliente}
              tiposIdentificacion={tiposIdentificacion.filter(ti => ti.Activo)}
              agregarError={agregarError}
              errores={errores}
              renovarCliente={renovarCliente}
              enRenovacionCliente={enRenovacionCliente}
              proyecto={proyecto}
              consultarClientePorIdentificacion={consultarClientePorIdentificacion}
              clienteConsultadoIdentificacion={clienteConsultadoIdentificacion}
              limpiarCampos={limpiarCampos}
              agregarErrorContacto={agregarErrorContacto}
              erroresContactos={erroresContactos}
              consultarClienteActivo={consultarClienteActivo}
            />
          </Box>
        </Tabs.Panel>
        <Tabs.Panel value="Historico">
          <Box pt="xs">
            <TablaClientesVista
              tabla={tabla}
              consultarClientes={consultarClientes}
              filtro={filtro}
              manejarHabilitarFiltros={manejarHabilitarFiltros}
              habilitarFiltros={habilitarFiltros}
              manejarCambioFiltroCliente={manejarCambioFiltroCliente}
              indicePagina={indicePagina}
              TAMANIO_PAGINA={TAMANIO_PAGINA}
            />
          </Box>
        </Tabs.Panel>
      </Tabs>
    </Box>
  )
}

export default React.memo(ClientesCV);